import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, CalendarRange, Check, Copy, ExternalLink, Film, Hash,
  Image as ImageIcon, Loader2, Pencil, Sparkles, Trash2, X, ZoomIn,
} from 'lucide-react';
import { labApi } from '@/lib/api';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Select } from '@/components/ui/Select';
import { ImageLightbox } from './ImageLightbox';

// SOCIAL CONTENT MACHINE - planer miesięczny: posty z pipeline'u SOCIAL_PLAN
// (podpisy IG/FB, hashtagi, prompty) + media generowane w workerze
// (grafika gpt-image; reels = kadr gpt-image animowany przez Higgsfield DoP).

interface SocialPostRecord {
  id: string;
  month: string;
  scheduledDate: string;
  scheduledTime: string;
  slotIndex: number;
  batchIndex: number;
  platform: string;
  postType: string;
  goal: string;
  experimentGroup: string | null;
  experimentRole: string;
  testVariable: string;
  pillar: string | null;
  topic: string | null;
  hook: string | null;
  caption: string | null;
  captionFb: string | null;
  hashtags: string | null;
  cta: string | null;
  onImageText: string | null;
  imagePrompt: string | null;
  videoPrompt: string | null;
  slides: Array<{ slide: number; image_prompt: string; on_image_text: string }> | null;
  format: string;
  approvalStatus: string;
  qualityScore: number;
  qualityIssues: Array<{ code: string; severity: 'warning' | 'error'; message: string }>;
  publishStatus: string;
  publishAttempts: number;
  publishError: string | null;
  facebookPostId: string | null;
  instagramMediaId: string | null;
  publishedAt: string | null;
  destinationUrl: string | null;
  trackingToken: string | null;
  clicksCount: number;
  attributedOrders: number;
  attributedRevenue: string | number;
  insights: Array<{
    windowHours: number;
    views: number;
    reach: number;
    likes: number;
    comments: number;
    saves: number;
    shares: number;
    totalInteractions: number;
    avgWatchTimeMs: number | null;
    engagementRate: number;
    performanceScore: number;
    capturedAt: string;
  }>;
  status: string;
  mediaError: string | null;
  mediaQualityStatus: string;
  mediaQualityIssues: string[];
  mediaWidth: number | null;
  mediaHeight: number | null;
  driveFileUrl: string | null;
  videoDriveFileUrl: string | null;
  updatedAt: string;
}

interface SocialPlannerPanelProps {
  productId: string;
}

interface SocialChannel {
  pageId: string;
  pageName: string;
  instagramAccountId: string | null;
  instagramUsername: string | null;
}

const POLL_INTERVAL_MS = 8000;

const POST_TYPE_LABELS: Record<string, string> = {
  IMAGE: 'Grafika',
  REEL: 'Reels',
  CAROUSEL: 'Karuzela',
  STORY: 'Story',
};

const PILLAR_LABELS: Record<string, string> = {
  EDUKACJA: 'Edukacja',
  'SOCIAL PROOF': 'Social proof',
  PRODUKT: 'Produkt',
  LIFESTYLE: 'Lifestyle',
  ENGAGEMENT: 'Engagement',
};

const GOAL_LABELS: Record<string, string> = {
  REACH: 'Zasięg',
  SAVES: 'Zapisy',
  ENGAGEMENT: 'Zaangażowanie',
  CONVERSION: 'Sprzedaż',
};

const QA_LABELS: Record<string, string> = {
  QA_PASSED: 'QA zaliczone',
  REVIEW: 'Do sprawdzenia',
  APPROVED: 'Zatwierdzony',
  REJECTED: 'Odrzucony',
};

const WEEKDAYS = ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'];

function dateLabel(date: string): string {
  const parsed = new Date(`${date}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return `${date} (${WEEKDAYS[parsed.getDay()]})`;
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (typeof error !== 'object' || error === null || !('response' in error)) return fallback;
  const response = (error as { response?: { data?: { error?: unknown } } }).response;
  return typeof response?.data?.error === 'string' ? response.data.error : fallback;
}

export function SocialPlannerPanel({ productId }: SocialPlannerPanelProps) {
  const [months, setMonths] = useState<string[]>([]);
  const [activeMonth, setActiveMonth] = useState<string | null>(null);
  const [posts, setPosts] = useState<SocialPostRecord[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SocialPostRecord | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCaption, setEditCaption] = useState('');
  const [editCaptionFb, setEditCaptionFb] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [queueingAll, setQueueingAll] = useState(false);
  const [approvingMonth, setApprovingMonth] = useState(false);
  const [schedulingMonth, setSchedulingMonth] = useState(false);
  const [channels, setChannels] = useState<SocialChannel[]>([]);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [channelError, setChannelError] = useState<string | null>(null);
  const [savingChannel, setSavingChannel] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadPosts = useCallback(async (month?: string | null) => {
    try {
      const res = await labApi.getSocialPosts(productId, month);
      const data = res.data.data || {};
      const loadedMonths: string[] = data.months || [];
      setMonths(loadedMonths);
      setPosts(data.posts || []);
      setActiveMonth(prev => {
        if (month) return month;
        if (prev && loadedMonths.includes(prev)) return prev;
        return loadedMonths[0] || null;
      });
    } catch (err) {
      console.error('Failed to load social posts:', err);
    } finally {
      setLoaded(true);
    }
  }, [productId]);

  useEffect(() => {
    void loadPosts(null);
  }, [loadPosts]);

  useEffect(() => {
    let active = true;
    labApi.getSocialChannels(productId)
      .then(res => {
        if (!active) return;
        const data = res.data.data || {};
        setChannels(data.channels || []);
        setSelectedPageId(data.selected?.socialFacebookPageId || '');
        setChannelError(null);
      })
      .catch(err => {
        if (!active) return;
        setChannelError(apiErrorMessage(err, 'Połącz ponownie konto Meta z uprawnieniami publikacji.'));
      });
    return () => { active = false; };
  }, [productId]);

  const handleChannelChange = async (pageId: string) => {
    setSelectedPageId(pageId);
    if (!pageId) return;
    setSavingChannel(true);
    try {
      await labApi.configureSocialChannel(productId, pageId);
      setChannelError(null);
    } catch (err) {
      setChannelError(apiErrorMessage(err, 'Nie udało się zapisać kanału publikacji.'));
    } finally {
      setSavingChannel(false);
    }
  };

  const monthPosts = useMemo(
    () => posts.filter(post => !activeMonth || post.month === activeMonth),
    [posts, activeMonth],
  );

  const hasPending = monthPosts.some(post => ['MEDIA_QUEUED', 'MEDIA_PROCESSING', 'MEDIA_GENERATING'].includes(post.status));
  useEffect(() => {
    if (!hasPending) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(() => void loadPosts(activeMonth), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [hasPending, loadPosts, activeMonth]);

  const copyToClipboard = (key: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleGenerateOne = async (post: SocialPostRecord) => {
    try {
      await labApi.generateSocialMedia(productId, post.id);
      await loadPosts(activeMonth);
    } catch (err) {
      console.error('Failed to queue social media:', err);
    }
  };

  const handleGenerateAll = async () => {
    if (!activeMonth) return;
    setQueueingAll(true);
    try {
      await labApi.generateAllSocialMedia(productId, activeMonth);
      await loadPosts(activeMonth);
    } catch (err) {
      console.error('Failed to queue all social media:', err);
    } finally {
      setQueueingAll(false);
    }
  };

  const handleApproveMonth = async () => {
    if (!activeMonth) return;
    setApprovingMonth(true);
    try {
      await labApi.approveSocialMonth(productId, activeMonth);
      await loadPosts(activeMonth);
    } catch (err) {
      console.error('Failed to approve social month:', err);
    } finally {
      setApprovingMonth(false);
    }
  };

  const handleApproval = async (post: SocialPostRecord, status: 'APPROVED' | 'REJECTED') => {
    try {
      await labApi.setSocialPostApproval(productId, post.id, status);
      await loadPosts(activeMonth);
    } catch (err) {
      console.error('Failed to update social approval:', err);
    }
  };

  const handleScheduleMonth = async () => {
    if (!activeMonth) return;
    setSchedulingMonth(true);
    try {
      await labApi.scheduleSocialMonth(productId, activeMonth);
      await loadPosts(activeMonth);
    } catch (err) {
      console.error('Failed to schedule social month:', err);
    } finally {
      setSchedulingMonth(false);
    }
  };

  const startEdit = (post: SocialPostRecord) => {
    setEditingId(post.id);
    setEditCaption(post.caption || '');
    setEditCaptionFb(post.captionFb || '');
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSavingEdit(true);
    try {
      await labApi.updateSocialPost(productId, editingId, { caption: editCaption, captionFb: editCaptionFb });
      setEditingId(null);
      await loadPosts(activeMonth);
    } catch (err) {
      console.error('Failed to save social post:', err);
    } finally {
      setSavingEdit(false);
    }
  };

  if (!loaded || posts.length === 0) return null;

  const readyCount = monthPosts.filter(post => post.status === 'READY').length;
  const queueableCount = monthPosts.filter(post => (
    ['CONTENT_READY', 'FAILED'].includes(post.status)
    && ['QA_PASSED', 'APPROVED'].includes(post.approvalStatus)
    && post.imagePrompt
  )).length;
  const qaPassedCount = monthPosts.filter(post => post.approvalStatus === 'QA_PASSED').length;
  const schedulableCount = monthPosts.filter(post => (
    post.approvalStatus === 'APPROVED'
    && post.status === 'READY'
    && ['NOT_SCHEDULED', 'PUBLISH_FAILED'].includes(post.publishStatus)
  )).length;

  // Grupowanie po dacie
  const groupedByDate = monthPosts.reduce<Record<string, SocialPostRecord[]>>((acc, post) => {
    (acc[post.scheduledDate] ||= []).push(post);
    return acc;
  }, {});
  const sortedDates = Object.keys(groupedByDate).sort();

  return (
    <div className="bg-surface-raised rounded-xl border border-border p-4 space-y-3">
      <div className="flex items-center gap-2.5 flex-wrap">
        <CalendarRange className="w-4 h-4 text-accent-secondary shrink-0" />
        <span className="text-sm font-semibold text-text-primary">Harmonogram publikacji</span>
        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-surface-hover border border-border-subtle text-text-secondary">
          media: {readyCount}/{monthPosts.length}
        </span>
        {hasPending && <Loader2 className="w-3 h-3 animate-spin text-warning shrink-0" />}
        <div className="flex items-center gap-1.5 ml-auto flex-wrap">
          {months.map(month => (
            <button
              key={month}
              onClick={() => {
                setActiveMonth(month);
                void loadPosts(month);
              }}
              className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                activeMonth === month
                  ? 'bg-accent-secondary/10 border-accent-secondary/40 text-text-primary'
                  : 'bg-surface-hover border-border text-text-muted hover:text-text-primary'
              }`}
            >
              {month}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap rounded-lg border border-border-subtle bg-surface-base/30 p-2.5">
        <span className="text-[10px] font-medium text-text-secondary">Kanał publikacji</span>
        <Select
          value={selectedPageId}
          onChange={event => void handleChannelChange(event.target.value)}
          disabled={savingChannel || channels.length === 0}
          className="min-w-64 py-1.5 text-xs"
          placeholder="Wybierz stronę Facebook"
        >
          <option value="">Wybierz stronę Facebook</option>
          {channels.map(channel => (
            <option key={channel.pageId} value={channel.pageId}>
              {channel.pageName}{channel.instagramUsername ? ` + @${channel.instagramUsername}` : ' (bez Instagram)'}
            </option>
          ))}
        </Select>
        {savingChannel && <Loader2 className="w-3.5 h-3.5 animate-spin text-warning" />}
        {channelError && <span className="text-[10px] text-warning">{channelError}</span>}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={handleApproveMonth}
          disabled={approvingMonth || qaPassedCount === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-success bg-success/10 border border-success/20 rounded-lg hover:bg-success/15 transition-all disabled:opacity-40"
        >
          {approvingMonth ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Zatwierdź posty po QA{qaPassedCount > 0 ? ` (${qaPassedCount})` : ''}
        </button>
        <button
          onClick={handleGenerateAll}
          disabled={queueingAll || queueableCount === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent-secondary bg-accent-secondary/10 border border-accent-secondary/20 rounded-lg hover:bg-accent-secondary/15 transition-all disabled:opacity-40"
        >
          {queueingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          Generuj wszystkie media{queueableCount > 0 ? ` (${queueableCount})` : ''}
        </button>
        <button
          onClick={handleScheduleMonth}
          disabled={schedulingMonth || schedulableCount === 0 || !selectedPageId}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent-primary bg-accent-primary/10 border border-accent-primary/20 rounded-lg hover:bg-accent-primary/15 transition-all disabled:opacity-40"
        >
          {schedulingMonth ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarRange className="w-3.5 h-3.5" />}
          Zaplanuj publikację{schedulableCount > 0 ? ` (${schedulableCount})` : ''}
        </button>
        <span className="text-[10px] text-text-muted">
          Grafiki: gpt-image + packshot jako referencja. Reels: kadr bazowy animowany przez Higgsfield. Kolejny przebieg planera na ten sam miesiąc dokłada posty (dogrywanie tematów).
        </span>
      </div>

      <div className="space-y-3">
        {sortedDates.map(date => (
          <div key={date}>
            <div className="text-[10px] font-semibold text-text-muted uppercase tracking-wide mb-1.5">{dateLabel(date)}</div>
            <div className="space-y-2">
              {groupedByDate[date].map(post => {
                const imageUrl = labApi.getSocialPostImageUrl(productId, post.id, post.updatedAt);
                const isBusy = ['MEDIA_QUEUED', 'MEDIA_PROCESSING', 'MEDIA_GENERATING'].includes(post.status);
                const hasImage = post.status === 'READY' || (post.status === 'MEDIA_GENERATING' && post.postType === 'REEL');
                const isExpanded = expandedId === post.id;
                const isEditing = editingId === post.id;
                return (
                  <div key={post.id} className="rounded-lg border border-border-subtle bg-surface-hover/30 p-2.5 flex gap-3">
                    <div className={`w-20 shrink-0 ${post.format === '1:1' ? 'aspect-square' : post.format === '9:16' ? 'aspect-[9/16]' : 'aspect-[4/5]'} rounded-md overflow-hidden bg-surface-hover relative group`}>
                      {hasImage ? (
                        <>
                          <img src={imageUrl} alt={post.topic || post.scheduledDate} loading="lazy" className="w-full h-full object-cover" />
                          <button
                            onClick={() => setLightbox({ src: imageUrl, alt: post.topic || post.scheduledDate })}
                            className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Podgląd"
                            aria-label="Podgląd grafiki posta"
                          >
                            <ZoomIn className="w-4 h-4 text-white" />
                          </button>
                        </>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-1 px-1 text-center" title={post.mediaError || undefined}>
                          {isBusy
                            ? <Loader2 className="w-4 h-4 animate-spin text-warning" />
                            : post.status === 'FAILED'
                              ? <AlertCircle className="w-4 h-4 text-red-400" />
                              : post.postType === 'REEL'
                                ? <Film className="w-4 h-4 text-text-disabled" />
                                : <ImageIcon className="w-4 h-4 text-text-disabled" />}
                          <span className="text-[9px] text-text-muted">
                            {isBusy ? (post.status === 'MEDIA_GENERATING' ? 'Wideo...' : 'Kadr...') : post.status === 'FAILED' ? 'Błąd' : 'Brak mediów'}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-accent-secondary/10 border border-accent-secondary/20 text-accent-secondary font-medium">
                              {POST_TYPE_LABELS[post.postType] || post.postType}
                            </span>
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-surface-raised border border-border-subtle text-text-secondary">{post.platform}</span>
                            {post.pillar && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] bg-surface-raised border border-border-subtle text-text-muted">
                                {PILLAR_LABELS[post.pillar.toUpperCase()] || post.pillar}
                              </span>
                            )}
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-surface-raised border border-border-subtle text-text-muted">{post.format}</span>
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-surface-raised border border-border-subtle text-text-muted">
                              {GOAL_LABELS[post.goal] || post.goal}
                            </span>
                            {post.experimentRole !== 'NONE' && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] bg-accent-info/10 border border-accent-info/20 text-accent-info">
                                Test {post.experimentRole === 'CONTROL' ? 'A' : 'B'}: {post.testVariable}
                              </span>
                            )}
                            <span className={`px-1.5 py-0.5 rounded text-[9px] border font-medium ${
                              post.approvalStatus === 'QA_PASSED' || post.approvalStatus === 'APPROVED'
                                ? 'bg-success/10 border-success/20 text-success'
                                : post.approvalStatus === 'REJECTED'
                                  ? 'bg-red-400/10 border-red-400/20 text-red-400'
                                  : 'bg-warning/10 border-warning/20 text-warning'
                            }`}>
                              {QA_LABELS[post.approvalStatus] || post.approvalStatus} {post.qualityScore}/100
                            </span>
                            {post.publishStatus !== 'NOT_SCHEDULED' && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] bg-accent-primary/10 border border-accent-primary/20 text-accent-primary">
                                {post.publishStatus === 'SCHEDULED' ? `Zaplanowany ${post.scheduledTime}` : post.publishStatus}
                              </span>
                            )}
                            {post.mediaQualityStatus === 'PASSED' && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] bg-success/10 border border-success/20 text-success">
                                Media QA {post.mediaWidth}x{post.mediaHeight}
                              </span>
                            )}
                          </div>
                          <p className="text-xs font-medium text-text-primary mt-1 line-clamp-1">{post.topic || post.hook}</p>
                          {post.hook && post.topic && (
                            <p className="text-[10px] text-text-secondary mt-0.5 line-clamp-1"><span className="text-text-muted">Hook:</span> {post.hook}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {post.videoDriveFileUrl && (
                            <a href={post.videoDriveFileUrl} target="_blank" rel="noreferrer" className="p-1 rounded text-text-muted hover:text-accent-primary transition-colors" title="Wideo na Google Drive" aria-label="Wideo na Google Drive">
                              <Film className="w-3.5 h-3.5" />
                            </a>
                          )}
                          {post.driveFileUrl && (
                            <a href={post.driveFileUrl} target="_blank" rel="noreferrer" className="p-1 rounded text-text-muted hover:text-accent-primary transition-colors" title="Grafika na Google Drive" aria-label="Grafika na Google Drive">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                          <button onClick={() => startEdit(post)} disabled={isBusy} className="p-1 rounded text-text-muted hover:text-accent-primary transition-colors disabled:opacity-40" title="Edytuj podpisy" aria-label="Edytuj podpisy">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          {post.approvalStatus !== 'APPROVED' && (
                            <button onClick={() => void handleApproval(post, 'APPROVED')} disabled={isBusy} className="p-1 rounded text-text-muted hover:text-success transition-colors disabled:opacity-40" title="Zatwierdź post" aria-label="Zatwierdź post">
                              <Check className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {post.approvalStatus !== 'REJECTED' && (
                            <button onClick={() => void handleApproval(post, 'REJECTED')} disabled={isBusy} className="p-1 rounded text-text-muted hover:text-red-400 transition-colors disabled:opacity-40" title="Odrzuć post" aria-label="Odrzuć post">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button onClick={() => setDeleteTarget(post)} disabled={isBusy} className="p-1 rounded text-text-muted hover:text-red-400 transition-colors disabled:opacity-40" title="Usuń" aria-label="Usuń post">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      {post.status === 'FAILED' && post.mediaError && (
                        <div className="flex items-start gap-1.5 mt-1.5 text-[10px] text-red-400">
                          <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" /> {post.mediaError}
                        </div>
                      )}
                      {post.mediaQualityStatus === 'FAILED' && post.mediaQualityIssues.length > 0 && (
                        <div className="mt-1.5 text-[10px] text-red-400">
                          Kontrola mediów: {post.mediaQualityIssues.join(' ')}
                        </div>
                      )}
                      {post.publishStatus === 'PUBLISH_FAILED' && post.publishError && (
                        <div className="flex items-start gap-1.5 mt-1.5 text-[10px] text-red-400">
                          <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" /> {post.publishError}
                        </div>
                      )}

                      {isEditing ? (
                        <div className="mt-2 space-y-2">
                          <label className="block">
                            <span className="text-[10px] font-medium text-text-secondary block mb-1">Podpis Instagram</span>
                            <textarea value={editCaption} onChange={e => setEditCaption(e.target.value)} rows={4} className="w-full text-[11px] bg-surface-base/60 border border-border rounded-lg px-2.5 py-2 text-text-primary focus:outline-none focus:border-accent-primary/40" />
                          </label>
                          <label className="block">
                            <span className="text-[10px] font-medium text-text-secondary block mb-1">Podpis Facebook</span>
                            <textarea value={editCaptionFb} onChange={e => setEditCaptionFb(e.target.value)} rows={3} className="w-full text-[11px] bg-surface-base/60 border border-border rounded-lg px-2.5 py-2 text-text-primary focus:outline-none focus:border-accent-primary/40" />
                          </label>
                          <div className="flex items-center gap-2">
                            <button onClick={saveEdit} disabled={savingEdit} className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40">
                              {savingEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Zapisz
                            </button>
                            <button onClick={() => setEditingId(null)} className="flex items-center gap-1 px-3 py-1.5 text-[11px] text-text-secondary hover:text-text-primary transition-colors">
                              <X className="w-3 h-3" /> Anuluj
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => handleGenerateOne(post)}
                            disabled={isBusy || !post.imagePrompt}
                            className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-accent-secondary bg-accent-secondary/10 border border-accent-secondary/20 rounded-lg hover:bg-accent-secondary/15 transition-all disabled:opacity-40"
                          >
                            {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : post.postType === 'REEL' ? <Film className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                            {isBusy ? 'Generowanie...' : post.status === 'READY' ? 'Przegeneruj media' : 'Generuj media'}
                          </button>
                          {post.caption && (
                            <button onClick={() => copyToClipboard(`ig-${post.id}`, post.caption!)} className="flex items-center gap-1 px-2.5 py-1 text-[11px] text-text-secondary bg-surface-raised border border-border-subtle rounded-lg hover:text-text-primary transition-colors">
                              {copiedKey === `ig-${post.id}` ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />} Podpis IG
                            </button>
                          )}
                          {post.captionFb && (
                            <button onClick={() => copyToClipboard(`fb-${post.id}`, post.captionFb!)} className="flex items-center gap-1 px-2.5 py-1 text-[11px] text-text-secondary bg-surface-raised border border-border-subtle rounded-lg hover:text-text-primary transition-colors">
                              {copiedKey === `fb-${post.id}` ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />} Podpis FB
                            </button>
                          )}
                          {post.hashtags && (
                            <button onClick={() => copyToClipboard(`ht-${post.id}`, post.hashtags!)} className="flex items-center gap-1 px-2.5 py-1 text-[11px] text-text-secondary bg-surface-raised border border-border-subtle rounded-lg hover:text-text-primary transition-colors">
                              {copiedKey === `ht-${post.id}` ? <Check className="w-3 h-3 text-success" /> : <Hash className="w-3 h-3" />} Hashtagi
                            </button>
                          )}
                          <button onClick={() => setExpandedId(prev => (prev === post.id ? null : post.id))} className="text-[11px] text-text-muted hover:text-text-primary transition-colors">
                            {isExpanded ? 'Zwiń' : 'Szczegóły'}
                          </button>
                        </div>
                      )}

                      {isExpanded && !isEditing && (
                        <div className="mt-2 space-y-1.5">
                          {post.caption && (
                            <pre className="bg-surface-base/60 rounded-lg p-2.5 text-[10px] text-text-secondary whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">{post.caption}</pre>
                          )}
                          {post.hashtags && <p className="text-[10px] text-accent-info">{post.hashtags}</p>}
                          {post.imagePrompt && (
                            <div className="flex items-start gap-1.5">
                              <p className="text-[10px] text-text-muted flex-1"><span className="font-medium">Prompt grafiki:</span> {post.imagePrompt}</p>
                              <button onClick={() => copyToClipboard(`ip-${post.id}`, post.imagePrompt!)} className="p-1 rounded text-text-muted hover:text-accent-primary transition-colors shrink-0" title="Kopiuj prompt" aria-label="Kopiuj prompt grafiki">
                                {copiedKey === `ip-${post.id}` ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
                              </button>
                            </div>
                          )}
                          {post.videoPrompt && <p className="text-[10px] text-text-muted"><span className="font-medium">Motion prompt:</span> {post.videoPrompt}</p>}
                          {post.slides && post.slides.length > 0 && (
                            <div className="text-[10px] text-text-muted space-y-1">
                              <span className="font-medium">Slajdy karuzeli ({post.slides.length}):</span>
                              {post.slides.map(slide => (
                                <p key={slide.slide} className="pl-2">#{slide.slide}: {slide.on_image_text || slide.image_prompt.slice(0, 120)}</p>
                              ))}
                            </div>
                          )}
                          {post.cta && <p className="text-[10px] text-text-muted"><span className="font-medium">CTA:</span> {post.cta}</p>}
                          {post.insights[0] && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 rounded-lg border border-accent-primary/15 bg-accent-primary/5 p-2">
                              <p className="text-[10px] text-text-secondary">Wynik <span className="font-semibold text-accent-primary">{post.insights[0].performanceScore}/100</span></p>
                              <p className="text-[10px] text-text-secondary">Zasięg <span className="font-semibold text-text-primary">{post.insights[0].reach}</span></p>
                              <p className="text-[10px] text-text-secondary">Interakcje <span className="font-semibold text-text-primary">{post.insights[0].totalInteractions}</span></p>
                              <p className="text-[10px] text-text-secondary">ER <span className="font-semibold text-text-primary">{post.insights[0].engagementRate}%</span></p>
                              <p className="text-[9px] text-text-muted col-span-full">Snapshot po {post.insights[0].windowHours} h</p>
                            </div>
                          )}
                          {(post.clicksCount > 0 || post.attributedOrders > 0) && (
                            <div className="grid grid-cols-3 gap-1.5 rounded-lg border border-success/15 bg-success/5 p-2">
                              <p className="text-[10px] text-text-secondary">Kliknięcia <span className="font-semibold text-text-primary">{post.clicksCount}</span></p>
                              <p className="text-[10px] text-text-secondary">Zamówienia <span className="font-semibold text-text-primary">{post.attributedOrders}</span></p>
                              <p className="text-[10px] text-text-secondary">Przychód <span className="font-semibold text-success">{Number(post.attributedRevenue).toFixed(2)} zł</span></p>
                            </div>
                          )}
                          {post.qualityIssues.length > 0 && (
                            <div className="rounded-lg border border-warning/20 bg-warning/5 p-2 space-y-1">
                              <span className="text-[10px] font-medium text-warning">Kontrola jakości</span>
                              {post.qualityIssues.map(issue => (
                                <p key={issue.code} className={issue.severity === 'error' ? 'text-[10px] text-red-400' : 'text-[10px] text-warning'}>
                                  {issue.message}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title="Usunąć post z harmonogramu?"
        message={deleteTarget ? <>Czy na pewno chcesz usunąć post z {deleteTarget.scheduledDate} ({POST_TYPE_LABELS[deleteTarget.postType] || deleteTarget.postType})?</> : ''}
        confirmLabel="Usuń"
        danger
        onConfirm={async () => {
          if (deleteTarget) await labApi.deleteSocialPost(productId, deleteTarget.id);
          setDeleteTarget(null);
          await loadPosts(activeMonth);
        }}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
